import Colors from "@/p5/colors";
import { circularIn } from "@/p5/easings";

const SHIMMER_LONG_START_DURATION = 5000;
const SHIMMER_LONG_PAUSE_DURATION = 1000;
const SHIMMER_LONG_END_DURATION = 5000;

const SHIMMER_SHORT_START_DURATION = 1000;
const SHIMMER_SHORT_PAUSE_DURATION = 1000;
const SHIMMER_SHORT_END_DURATION = 1000;

/**
 * Management class for drawing a cube/square onto a background graphics layer. This is the base layer that is passed into
 * the shader and distorted + ASCIIified.
 *
 * The colors present in this base layer will be reflected in the text ASCII colors of the final output image.
 */
class SrcRenderer {
  constructor(p, timer, staticPositionOffset, enableShimmer, shimmerStart, colors, shimmerColor) {
    this.p = p;
    this.layer = this.p.createGraphics(this.p.width, this.p.height, this.p.WEBGL);

    this.faceLayer = this.p.createGraphics(100, 100);
    this.lastShimmerStart = null;
    this.nextShimmerStart = shimmerStart;
    this.longShimmer = true;
    this.staticPositionOffset = staticPositionOffset;

    this.layer.pixelDensity(1);
    this.timer = timer;
    this.colors = colors;
    this.enableShimmer = enableShimmer;
    this.mx = this.p.width / 2;
    this.my = this.p.height / 2;
    this.tmx = this.mx;
    this.tmy = this.my;
    this.shimmerColor = shimmerColor;
  }

  shimmerStart() {
    return (
      this.lastShimmerStart +
      (this.longShimmer ? SHIMMER_LONG_START_DURATION : SHIMMER_SHORT_START_DURATION)
    );
  }

  shimmerPause() {
    return (
      this.shimmerStart() +
      (this.longShimmer ? SHIMMER_LONG_PAUSE_DURATION : SHIMMER_SHORT_PAUSE_DURATION)
    );
  }

  shimmerEnd() {
    return (
      this.shimmerPause() +
      (this.longShimmer ? SHIMMER_LONG_END_DURATION : SHIMMER_SHORT_END_DURATION)
    );
  }

  drawFaceLayer(originalColor) {
    if (this.p.millis() > this.nextShimmerStart) {
      this.nextShimmerStart = this.p.millis() + this.p.random(16000, 24000);
      this.lastShimmerStart = this.p.millis();
      this.longShimmer = this.timer.targetChaos === 1.0;
    } else if (this.lastShimmerStart !== null && this.p.millis() < this.shimmerStart()) {
      let shimmerRadius = this.timer.eased(
        circularIn,
        this.p.millis(),
        this.lastShimmerStart,
        this.shimmerStart(),
        0,
        this.p.width
      );

      this.faceLayer.background(originalColor);
      this.faceLayer.noStroke();
      this.faceLayer.fill(this.shimmerColor);
      this.faceLayer.circle(0, 0, shimmerRadius);
    } else if (this.lastShimmerStart !== null && this.p.millis() < this.shimmerPause()) {
      this.faceLayer.background(originalColor);
      this.faceLayer.noStroke();
      this.faceLayer.fill(this.shimmerColor);
      this.faceLayer.circle(0, 0, this.p.width);
    } else if (this.lastShimmerStart !== null && this.p.millis() < this.shimmerEnd()) {
      let shimmerRadius = this.timer.eased(
        circularIn,
        this.p.millis(),
        this.shimmerPause(),
        this.shimmerEnd(),
        0,
        this.p.width
      );

      this.faceLayer.background(this.shimmerColor);
      this.faceLayer.noStroke();
      this.faceLayer.fill(originalColor);
      this.faceLayer.circle(0, 0, shimmerRadius);
    } else {
      this.faceLayer.background(originalColor);
    }
  }

  render(enableMouse, isMouseEntered) {
    let faceVid = this.faceLayer;
    this.layer.background("#00000011");

    if (!this.enableShimmer) {
      this.layer.stroke(Colors.sub);
      this.layer.strokeWeight(Math.max(0, 20 - 30 * (1.0 - this.timer.dScale())));
      this.layer.noStroke();
    } else {
      this.layer.stroke(Colors.h1);
      this.layer.strokeWeight(20);
      this.layer.noFill();
    }

    this.layer.push();
    this.layer.translate(0, 0, -200 * this.timer.dScale());

    if (enableMouse) {
      this.layer.translate(
        this.mx - this.p.width / 2,
        this.my - this.p.height / 2 - 200 * (1.0 - this.timer.dScale())
      );

      if (isMouseEntered) {
        // Follow mouse
        this.mx += 0.1 * (this.p.mouseX - this.mx);

        // Keep the y value lower on the screen to avoid dead spaces
        const clampedY = this.p.map(this.p.mouseY, 0, this.p.height, 200, this.p.height, true);

        this.my += 0.1 * (clampedY - this.my);
      } else {
        // Ensure the default position is in the center if the
        // mouse has not been detected in the sketch yet
        this.mx += 0.1 * (this.p.width / 2 - this.mx);
        this.my += 0.1 * (this.p.height / 2 + 200 * (1.0 - this.timer.dScale()) - this.my);
      }
    } else if (this.staticPositionOffset) {
      this.layer.translate(
        this.mx - this.p.width / 2 + this.staticPositionOffset.x,
        this.my - this.p.height / 2 + this.staticPositionOffset.y
      );
    } else {
      this.layer.translate(this.mx - this.p.width / 2, this.my - this.p.height / 2);

      if (this.timer.targetChaos !== 1.0 || this.timer.currentChaos > 0.5) {
        this.mx += 0.1 * (this.tmx - this.mx);
        this.my += 0.1 * (this.tmy - this.my);
      }
    }

    this.timer.applyRotation(this.layer);
    let boxsz = this.timer.wh();
    // let boxsz = this.p.width / 4;
    // this.layer.box(boxsz);
    let d = boxsz * this.timer.dScale();
    this.drawBoxPanels(boxsz, boxsz, d, faceVid);
    this.layer.pop();
  }

  drawBoxPanels(w, h, d, faceVid) {
    // Front?
    this.layer.textureMode(this.p.NORMAL);
    this.layer.angleMode(this.p.DEGREES);
    this.layer.noStroke();
    if (!this.enableShimmer) {
      this.layer.fill(this.colors[0]);
    } else {
      this.drawFaceLayer(this.colors[0]);
      this.layer.texture(faceVid);
    }
    this.layer.translate(-w / 2, -h / 2);
    this.layer.quad(0, 0, w, 0, w, h, 0, h);

    if (d <= 0.001) {
      return;
    }

    // Left
    if (!this.enableShimmer) {
      this.layer.fill(this.colors[1]);
    } else {
      this.drawFaceLayer(this.colors[1]);
    }
    this.layer.push();
    this.layer.translate(0, 0, -d);
    this.layer.rotateY(-90);
    this.layer.quad(0, 0, d, 0, d, h, 0, h);
    this.layer.pop();

    // Top
    if (!this.enableShimmer) {
      this.layer.fill(this.colors[2]);
    } else {
      this.drawFaceLayer(this.colors[2]);
    }
    this.layer.push();
    this.layer.translate(0, 0, -d);
    this.layer.rotateX(90);
    this.layer.quad(0, 0, w, 0, w, d, 0, d);
    this.layer.pop();

    // Right
    if (!this.enableShimmer) {
      this.layer.fill(this.colors[1]);
    } else {
      this.drawFaceLayer(this.colors[1]);
    }
    this.layer.push();
    this.layer.translate(w, 0, 0);
    this.layer.rotateY(90);
    this.layer.quad(0, 0, d, 0, d, h, 0, h);
    this.layer.pop();

    // Bottom
    if (!this.enableShimmer) {
      this.layer.fill(this.colors[2]);
    } else {
      this.drawFaceLayer(this.colors[2]);
    }
    this.layer.push();
    this.layer.translate(0, h, 0);
    this.layer.rotateX(-90);
    this.layer.quad(0, 0, w, 0, w, d, 0, d);
    this.layer.pop();

    // Back

    if (!this.enableShimmer) {
      this.layer.fill(this.colors[0]);
    } else {
      this.drawFaceLayer(this.colors[0]);
      this.layer.texture(faceVid);
    }
    this.layer.push();
    this.layer.rotateY(180);
    this.layer.translate(-w, 0, d);
    this.layer.quad(0, 0, w, 0, w, h, 0, h);
    this.layer.pop();
    this.layer.angleMode(this.p.RADIANS);
  }
}

export default SrcRenderer;
